import React, { useCallback, useEffect, useRef, HTMLProps, MouseEvent } from 'react';
import noop from 'lodash/noop';
import { createPortal } from 'react-dom';
import useIntersection from 'react-use/lib/useIntersection';
import useToggle from 'react-use/lib/useToggle';
import useVideo from 'react-use/lib/useVideo';
import styled from 'styled-components';
import { VideoStateEventParams, VideoStates } from '../../utils/analyticsService';
import { LinearProgress } from '../feedback/LinearProgress';
import { IconMute } from '../icon/IconMute';
import { IconPlay } from '../icon/IconPlay';
import { IconUnmute } from '../icon/IconUnmute';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';

const VideoTag = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: transparent;
    object-position: center;
    -webkit-user-drag: none;
    user-drag: none;
    user-select: none;
    transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;

function hasAudio(video: HTMLVideoElement): boolean {
    return (
        (video as any).mozHasAudio ||
        Boolean((video as any).webkitAudioDecodedByteCount) ||
        Boolean((video as any).audioTracks?.length)
    );
}

interface VideoProps extends HTMLProps<HTMLVideoElement> {
    autoplayThreshold?: number;
    firstFrameUrl?: string;
    onVideoStateChange?: (params: VideoStateEventParams) => void;
    soundContainer?: HTMLDivElement | null;
}

export function VideoComponent(props: VideoProps) {
    const {
        src,
        firstFrameUrl,
        autoplayThreshold = 0.5,
        onVideoStateChange = noop,
        className,
        soundContainer,
        ...other
    } = props;
    const isVisibleRef = useRef(false);
    const [wasStarted, setWasStarted] = useToggle(false);
    const [videoElement, videoState, videoController, videoRef] = useVideo(
        <VideoTag preload="auto" playsInline muted {...other}>
            <source src={src} />
        </VideoTag>,
    );

    const intersection = useIntersection(videoRef, {
        root: null,
        threshold: autoplayThreshold,
    });

    const onPlayPauseClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            onVideoStateChange({
                eventType: videoState.paused ? VideoStates.VIDEO_RESUMED : VideoStates.VIDEO_PAUSED,
            });

            if (videoState.paused) {
                videoController.play();

                return;
            }

            videoController.pause();
        },
        [videoController, videoState],
    );

    const onVolumeControlClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            if (videoState.muted) {
                videoController.unmute();
                onVideoStateChange({
                    eventType: VideoStates.VIDEO_UN_MUTED,
                });

                return;
            }

            videoController.mute();
            onVideoStateChange({
                eventType: VideoStates.VIDEO_MUTED,
            });
        },
        [videoController, videoState],
    );

    const onVideoStarted = useCallback(() => {
        onVideoStateChange({
            eventType: VideoStates.VIDEO_STARTED,
        });
        setWasStarted(true);
    }, [onVideoStateChange, setWasStarted]);

    const onVideoEnded = useCallback(() => {
        onVideoStateChange({
            eventType: VideoStates.VIDEO_FINISHED,
            wasNotTriggeredByUser: true,
        });
        videoRef.current?.play();
        onVideoStateChange({
            eventType: VideoStates.VIDEO_STARTED,
        });
    }, [videoRef.current, onVideoStateChange]);

    useEffect(() => {
        const onVideoLoaded = () => videoRef.current?.style.setProperty('opacity', '1');

        if (videoRef.current) {
            if (videoRef.current.readyState === 0 || videoRef.current.readyState === 1) {
                videoRef.current.style.setProperty('opacity', '0');
            }

            videoRef.current.addEventListener('loadeddata', onVideoLoaded);
            videoRef.current.addEventListener('timeupdate', onVideoStarted, { once: true });
            videoRef.current.addEventListener('ended', onVideoEnded);
        }

        return () => {
            videoRef.current?.removeEventListener('timeupdate', onVideoStarted);
            videoRef.current?.removeEventListener('ended', onVideoEnded);
            videoRef.current?.removeEventListener('loadeddata', onVideoLoaded);
        };
    }, [videoRef.current, onVideoStarted, onVideoEnded]);

    useEffect(() => {
        const isVisible = Boolean(intersection && intersection.intersectionRatio >= 0.5);

        if (isVisible !== isVisibleRef.current) {
            isVisibleRef.current = isVisible;

            if (wasStarted) {
                onVideoStateChange({
                    eventType: isVisible ? VideoStates.VIDEO_RESUMED : VideoStates.VIDEO_PAUSED,
                });
            }

            videoController[isVisible ? 'play' : 'pause']();
        }
    }, [
        wasStarted,
        videoController,
        isVisibleRef.current,
        intersection?.intersectionRatio,
        onVideoStateChange,
    ]);

    useEffect(() => {
        if (!videoRef.current || !src) {
            return;
        }

        videoRef.current.src = src;
        videoRef.current.play();
    }, [src]);

    const soundControl = videoRef.current && hasAudio(videoRef.current) && (
        <Box>
            <IconButton size="s" onClick={onVolumeControlClick}>
                {videoRef.current?.muted ? (
                    <IconMute color="#FFF" size={30} />
                ) : (
                    <IconUnmute color="#FFF" size={30} />
                )}
            </IconButton>
        </Box>
    );

    const progress = (videoState.time / videoState.duration) * 100;

    // Проверка на progress, чтобы конрол не мигал, пока видео не загрузилось
    const playPauseControlEnabled = videoRef.current && videoState.paused && progress > 1;

    return (
        <Box
            position="relative"
            className={className}
            onClick={onPlayPauseClick}
            style={{
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundImage: `url(${firstFrameUrl})`,
            }}
        >
            {videoElement}
            {soundContainer ? createPortal(soundControl, soundContainer) : soundControl}
            {playPauseControlEnabled && (
                <Box position="absolute" left="50%" top="50%" transform="translate(-50%, -50%)">
                    <IconButton variant="filled" size="xl" onClick={onPlayPauseClick}>
                        <IconPlay />
                    </IconButton>
                </Box>
            )}
            <LinearProgress position="absolute" height={4} bottom={0} progress={progress} />
        </Box>
    );
}
